import { useTranslation } from "react-i18next";

import type { ActivityTimeseriesPoint } from "@/lib/api";
import { formatDay } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Dependency-free bar chart of daily activity. Each bar's height is the total
 * number of events that day (secondary series, dc-blauw-licht); the main
 * series at its base is the share that were logins (De Ceuster blauw).
 * Square bars, with a 2 px gap between the stacked segments, per the De
 * Ceuster figure style.
 */
export function ActivityChart({ data }: { data: ActivityTimeseriesPoint[] }) {
  const { t } = useTranslation();
  const max = Math.max(1, ...data.map((d) => d.events));

  return (
    <div className="flex h-40 items-stretch gap-1">
      {data.map((point) => (
        <div key={point.day} className="group relative flex flex-1 flex-col justify-end">
          <div
            className="bg-chart-2 relative w-full"
            style={{ height: `${Math.max(2, (point.events / max) * 100)}%` }}
          >
            <div
              className={cn(
                "bg-chart-1 absolute inset-x-0 bottom-0",
                point.logins > 0 && point.logins < point.events && "border-card border-t-2",
              )}
              style={{
                height: `${point.events ? (point.logins / point.events) * 100 : 0}%`,
              }}
            />
          </div>
          <span className="bg-foreground text-background pointer-events-none absolute -top-7 left-1/2 z-10 hidden -translate-x-1/2 rounded-sm px-1.5 py-0.5 text-xs whitespace-nowrap group-hover:block">
            {t("activity.dayTooltip", {
              day: formatDay(point.day),
              events: t("activity.eventCount", { count: point.events }),
              logins: t("activity.loginCount", { count: point.logins }),
            })}
          </span>
        </div>
      ))}
    </div>
  );
}
